import { ArrowLeft, ChartLineUp, Clock } from "@phosphor-icons/react";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, NavLink, useParams } from "react-router";
import {
	useTenantApartmentById,
	useTenantElectricityChart,
	useTenantHeatChart,
	useTenantWaterChart,
} from "@/api/tenant/queries";
import { ApartmentInfoCard } from "@/components/tenant/apartment-info-card";
import { TenantMetersTable } from "@/components/tenant/tenant-meters-table";
import { TenantStatCards } from "@/components/tenant/tenant-stat-cards";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/format-datetime";

const TenantUsageChart = lazy(() =>
	import("@/components/tenant/tenant-usage-chart").then((m) => ({
		default: m.TenantUsageChart,
	})),
);

function getLatestCommunication(
	meters: Array<{ last_communication: string | null }>,
): string | null {
	const dates = meters
		.map((m) => m.last_communication)
		.filter((d): d is string => d !== null);
	if (dates.length === 0) return null;
	return dates.sort().at(-1) ?? null;
}

type FreshnessKey = "fresh" | "recent" | "stale" | "never";

function freshnessOf(iso: string | null): FreshnessKey {
	if (!iso) return "never";
	const ageMs = Date.now() - new Date(iso).getTime();
	const day = 24 * 60 * 60 * 1000;
	if (ageMs <= 2 * day) return "fresh";
	if (ageMs <= 14 * day) return "recent";
	return "stale";
}

export function MyApartmentDetailPage() {
	const { t } = useTranslation();
	const { id, lang } = useParams<{ id: string; lang: string }>();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";
	const apartmentId = Number(id);
	const { data: apartment, isLoading } = useTenantApartmentById(apartmentId);
	const { data: waterData, isLoading: waterLoading } = useTenantWaterChart();
	const { data: heatData, isLoading: heatLoading } = useTenantHeatChart();
	const { data: electricityData, isLoading: electricityLoading } =
		useTenantElectricityChart();
	const hideElectricity = useHideElectricity();

	if (Number.isNaN(apartmentId)) {
		return (
			<Navigate
				to={`/${currentLang}/${ROUTE_SLUGS.myApartments[currentLang]}`}
				replace
			/>
		);
	}

	if (isLoading) {
		return (
			<div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:p-6">
				<Skeleton className="h-9 w-32" />
				<div className="grid grid-cols-12 gap-4">
					<Skeleton className="col-span-12 h-32 rounded-xl lg:col-span-8" />
					<Skeleton className="col-span-12 h-32 rounded-xl lg:col-span-4" />
					<Skeleton className="col-span-6 h-28 rounded-xl lg:col-span-3" />
					<Skeleton className="col-span-6 h-28 rounded-xl lg:col-span-3" />
					<Skeleton className="col-span-6 h-28 rounded-xl lg:col-span-3" />
					<Skeleton className="col-span-6 h-28 rounded-xl lg:col-span-3" />
					<Skeleton className="col-span-12 h-64 rounded-xl" />
				</div>
			</div>
		);
	}

	if (!apartment) {
		return (
			<div className="flex flex-1 flex-col items-start gap-2 p-4 lg:p-6">
				<h1 className="text-2xl font-semibold">
					{t("tenant.detail.notFoundTitle")}
				</h1>
				<p className="text-muted-foreground">
					{t("tenant.detail.notFoundDescription")}
				</p>
			</div>
		);
	}

	const last = getLatestCommunication(apartment.meters);
	const freshness = freshnessOf(last);
	const statusTone: Record<FreshnessKey, string> = {
		fresh: "bg-emerald-500",
		recent: "bg-sky-500",
		stale: "bg-amber-500",
		never: "bg-muted-foreground/40",
	};
	const statusLabel: Record<FreshnessKey, string> = {
		fresh: t("tenant.list.statusFresh"),
		recent: t("tenant.list.statusRecent"),
		stale: t("tenant.list.statusStale"),
		never: t("tenant.list.statusNever"),
	};
	const rateColSpan = hideElectricity
		? "col-span-12 sm:col-span-4"
		: "col-span-6 sm:col-span-6 lg:col-span-3";

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:p-6">
			<div>
				<NavLink
					to={`/${currentLang}/${ROUTE_SLUGS.myApartments[currentLang]}`}
					className="inline-flex items-center gap-1.5 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<ArrowLeft className="size-4" />
					{t("tenant.detail.backToList")}
				</NavLink>
			</div>

			<div className="grid grid-cols-12 gap-3 sm:gap-4">
				<ApartmentInfoCard
					apartment={apartment}
					className="col-span-12 lg:col-span-8"
				/>

				<Card className="col-span-12 flex flex-col justify-between gap-3 border-border/60 p-5 sm:p-6 lg:col-span-4">
					<div className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
							<Clock weight="duotone" className="size-4" />
							<span>{t("tenant.list.lastCommunication")}</span>
						</div>
						<span className="flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
							<span
								className={cn(
									"size-2 rounded-full",
									statusTone[freshness],
									freshness === "fresh" && "animate-pulse",
								)}
								aria-hidden="true"
							/>
							{statusLabel[freshness]}
						</span>
					</div>
					<p className="text-xl font-semibold tabular-nums tracking-tight">
						{last
							? formatDateTime(last, currentLang)
							: t("tenant.list.neverCommunicated")}
					</p>
				</Card>

				<TenantStatCards apartment={apartment} itemClassName={rateColSpan} />

				<Card className="col-span-12 border-border/60 p-5 sm:p-6">
					<div className="mb-3 flex flex-col gap-1">
						<div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
							<ChartLineUp weight="duotone" className="size-4 text-primary" />
							<span>{t("tenant.charts.usage")}</span>
						</div>
						<p className="text-xs text-muted-foreground">
							{t("tenant.charts.aggregateNotice")}
						</p>
					</div>
					<Suspense fallback={<Skeleton className="h-48 w-full" />}>
						<div
							className={cn(
								"grid grid-cols-1 gap-3 sm:gap-4",
								hideElectricity ? "lg:grid-cols-2" : "lg:grid-cols-3",
							)}
						>
							<TenantUsageChart
								title={t("tenant.charts.water")}
								data={waterData}
								loading={waterLoading}
								unit="m³"
							/>
							<TenantUsageChart
								title={t("tenant.charts.heat")}
								data={heatData}
								loading={heatLoading}
								unit="GJ"
							/>
							{!hideElectricity && (
								<TenantUsageChart
									title={t("tenant.charts.electricity")}
									data={electricityData}
									loading={electricityLoading}
									unit="kWh"
								/>
							)}
						</div>
					</Suspense>
				</Card>

				<div className="col-span-12">
					<TenantMetersTable meters={apartment.meters} />
				</div>
			</div>
		</div>
	);
}
